/**
 * Entity types that can be linked via polymorphic link tables (event_link, issue_link).
 * This allows flexible entity associations without separate junction tables per type.
 */
export enum LinkableEntityType {
	CLIENT = 'client',
	VENDOR = 'vendor',
	OPPORTUNITY = 'opportunity',
	ORDER = 'order',
	QUOTE = 'quote',
	INVOICE = 'invoice',
	ITEM = 'item',
	ISSUE = 'issue',
	LICENSE = 'license',
	SOURCING_REQUEST = 'sourcing_request',
	VEHICLE = 'vehicle',
	MERCHANDISER = 'merchandiser',
	/** PD-15925 — a planned sales trip stop mirrored as a calendar event. */
	SALES_TRIP_STOP = 'sales_trip_stop',
	/** PD-15925 — the visit logged when a sales trip stop was completed. */
	CLIENT_VISIT = 'client_visit',
	/** PD-17132 — a person (contact) a task, note or issue is about. */
	CONTACT = 'contact',
}

export type LinkableEntityTypeValue =
	(typeof LinkableEntityType)[keyof typeof LinkableEntityType];

/**
 * Human-readable labels for linkable entity types
 */
export const LinkableEntityTypeLabels: Record<LinkableEntityType, string> = {
	[LinkableEntityType.CLIENT]: 'Client',
	[LinkableEntityType.VENDOR]: 'Vendor',
	[LinkableEntityType.OPPORTUNITY]: 'Opportunity',
	[LinkableEntityType.ORDER]: 'Order',
	[LinkableEntityType.QUOTE]: 'Quote',
	[LinkableEntityType.INVOICE]: 'Invoice',
	[LinkableEntityType.ITEM]: 'Item',
	[LinkableEntityType.ISSUE]: 'Issue',
	[LinkableEntityType.LICENSE]: 'License',
	[LinkableEntityType.SOURCING_REQUEST]: 'Sourcing Request',
	[LinkableEntityType.VEHICLE]: 'Vehicle',
	[LinkableEntityType.MERCHANDISER]: 'Merchandiser',
	[LinkableEntityType.SALES_TRIP_STOP]: 'Sales Trip Stop',
	[LinkableEntityType.CLIENT_VISIT]: 'Visit',
	[LinkableEntityType.CONTACT]: 'Person',
};

/**
 * Get human-readable label for an entity type
 */
export function getLinkableEntityTypeLabel(
	entityType: string | null | undefined,
): string {
	if (entityType == null) {
		return '-';
	}

	return (
		LinkableEntityTypeLabels[entityType as LinkableEntityType] ?? entityType
	);
}
